import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
} from '@repo/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowLeft,
  ClipboardList,
  FileText,
  Filter,
  Gauge,
  Truck,
  Wrench,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ReactNode } from 'react'
import type { DateRange } from 'react-day-picker'

import { DateRangePicker } from '@/components/shared/date-range-picker'
import { ListPagination } from '@/components/shared/list-pagination'
import { VehicleStatusBadge } from '@/components/shared/vehicle-status-badge'
import { DddFilesDownloadBar } from '@/features/tachograph/components/ddd-files-download-bar'
import { DddFilesTable } from '@/features/tachograph/components/ddd-files-table'
import {
  useDownloadDddReport,
  useDownloadFiles,
  useGetVehicleFiles,
} from '@/features/tachograph/hooks'
import { useVehicles } from '@/features/vehicles/hooks/use-vehicles'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { getManufacturerIcon } from '@/hooks/use-trucks-manufacturers'
import { localizedPageTitle } from '@/lib/page-title'

export const Route = createFileRoute(
  '/dashboard/tachograph-activities/$vehicleId',
)({
  head: () => ({ meta: [{ title: localizedPageTitle('vehicleActivity') }] }),
  component: VehicleActivityPage,
})

const DATA_TYPE_OPTIONS: Array<{
  value: string
  label: string
  icon: ReactNode
}> = [
  {
    value: 'Overview',
    label: 'Overview',
    icon: <FileText className="h-4 w-4" />,
  },
  {
    value: 'Activities',
    label: 'Activities',
    icon: <ClipboardList className="h-4 w-4" />,
  },
  {
    value: 'EventsAndFaults',
    label: 'Events & Faults',
    icon: <AlertTriangle className="h-4 w-4" />,
  },
  {
    value: 'DetailedSpeed',
    label: 'Detailed Speed',
    icon: <Gauge className="h-4 w-4" />,
  },
  {
    value: 'TechnicalData',
    label: 'Technical Data',
    icon: <Wrench className="h-4 w-4" />,
  },
]

function VehicleActivityPage() {
  useDocumentTitle('vehicleActivity')
  const { t } = useTranslation('tachographActivities')
  const { vehicleId } = Route.useParams()
  const numericId = Number(vehicleId)

  const [selectedFileIds, setSelectedFileIds] = useState<Array<string>>([])
  const [downloadRange, setDownloadRange] = useState<DateRange | undefined>()
  const [activityRange, setActivityRange] = useState<DateRange | undefined>()
  const [dataTypes, setDataTypes] = useState<Array<string>>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  const { data: vehicles } = useVehicles({ hasDevice: true })
  const vehicle = vehicles?.find((v) => v.id === numericId)

  // Download date filter is server-side (TachoSync API supports it)
  const { data: filesData, isLoading: filesLoading } = useGetVehicleFiles({
    pageNumber: page,
    pageSize,
    vehicleId: numericId,
    from: downloadRange?.from?.toISOString(),
    to: downloadRange?.to?.toISOString(),
  })

  // Activity period + data types are client-side filters
  const filteredFiles = useMemo(() => {
    let items = filesData?.items ?? []

    if (activityRange?.from) {
      const fromStr = activityRange.from.toISOString()
      items = items.filter(
        (item) =>
          !item.schedule?.activitiesFrom ||
          item.schedule.activitiesFrom >= fromStr,
      )
    }
    if (activityRange?.to) {
      const toStr = activityRange.to.toISOString()
      items = items.filter(
        (item) =>
          !item.schedule?.activitiesTo || item.schedule.activitiesTo <= toStr,
      )
    }

    if (dataTypes.length > 0) {
      items = items.filter((item) =>
        dataTypes.every((dt) => item.schedule?.fileTypes?.includes(dt)),
      )
    }

    return items
  }, [filesData, activityRange, dataTypes])

  const downloadMutation = useDownloadFiles()
  const reportMutation = useDownloadDddReport()

  const handleDownloadReport = (id: string) => {
    reportMutation.mutate({ type: 'vehicle', id })
  }

  const handleDownloadSingle = (id: string) => {
    downloadMutation.mutate({ type: 'vehicle', ids: [id] })
  }

  const handleDownloadSelected = () => {
    if (selectedFileIds.length > 0) {
      downloadMutation.mutate({
        type: 'vehicle',
        ids: selectedFileIds,
      })
    }
  }

  const toggleDataType = (type: string) => {
    setDataTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    )
  }

  const ManufacturerIcon = vehicle
    ? getManufacturerIcon(vehicle.manufacturer)
    : null

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link
            to="/dashboard/tachograph-activities"
            search={{ tab: 'vehicle' }}
          >
            <ArrowLeft className="size-4" />
          </Link>
        </Button>

        <div className="flex items-center gap-4">
          {vehicle ? (
            <>
              <div className="bg-background flex size-12 items-center justify-center rounded-md border">
                {ManufacturerIcon ? (
                  <ManufacturerIcon />
                ) : (
                  <Truck className="text-primary size-5" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-bold">{vehicle.licensePlate}</h1>
                  <VehicleStatusBadge status={vehicle.status} />
                </div>
                <p className="text-muted-foreground text-sm">
                  {vehicle.truck?.vehicleModel ?? vehicle.manufacturer}{' '}
                  {vehicle.vinNumber}
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-1">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          )}
        </div>
      </div>

      {/* DDD Files */}
      <Card className="max-md:rounded-none max-md:border-0 max-md:bg-transparent max-md:shadow-none">
        <CardHeader className="max-md:px-0">
          <CardTitle className="text-sm">{t('tachographFiles')}</CardTitle>
        </CardHeader>
        <CardContent className="max-md:px-0">
          {/* Files filters */}
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            {/* Download date — server-side */}
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-xs">
                {t('filesFilter.downloadDate')}:
              </span>
              <DateRangePicker
                value={downloadRange}
                onChange={setDownloadRange}
                placeholder={t('filesFilter.downloadDate')}
                className="h-8 text-xs"
              />
            </div>

            {/* Activity period — client-side */}
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-xs">
                {t('filesFilter.activityPeriod')}:
              </span>
              <DateRangePicker
                value={activityRange}
                onChange={setActivityRange}
                placeholder={t('filesFilter.activityPeriod')}
                className="h-8 text-xs"
              />
            </div>

            {/* Data types — client-side */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs">
                  <Filter className="mr-1.5 h-3.5 w-3.5" />
                  {t('filesFilter.dataTypes')}
                  {dataTypes.length > 0 && (
                    <span className="bg-primary text-primary-foreground ml-1.5 flex h-4 w-4 items-center justify-center rounded-full text-[10px]">
                      {dataTypes.length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="start">
                <div className="space-y-1">
                  {DATA_TYPE_OPTIONS.map(({ value, label, icon }) => (
                    <label
                      key={value}
                      className="hover:bg-muted flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm"
                    >
                      <Checkbox
                        checked={dataTypes.includes(value)}
                        onCheckedChange={() => toggleDataType(value)}
                      />
                      <span className="text-muted-foreground">{icon}</span>
                      {label}
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {(downloadRange || activityRange || dataTypes.length > 0) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                onClick={() => {
                  setDownloadRange(undefined)
                  setActivityRange(undefined)
                  setDataTypes([])
                }}
              >
                {t('filesFilter.clear')}
              </Button>
            )}
          </div>

          <DddFilesTable
            type="vehicle"
            items={filteredFiles}
            isLoading={filesLoading}
            onDownload={handleDownloadSingle}
            onDownloadReport={handleDownloadReport}
            reportPendingId={
              reportMutation.isPending
                ? (reportMutation.variables?.id ?? null)
                : null
            }
            selectedIds={selectedFileIds}
            onSelectionChange={setSelectedFileIds}
          />

          <ListPagination
            page={filesData?.page ?? 1}
            totalPages={filesData?.totalPages ?? 1}
            pageSize={pageSize}
            totalCount={filesData?.totalCount ?? 0}
            hasPrevious={filesData?.hasPrevious ?? false}
            hasNext={filesData?.hasNext ?? false}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s)
              setPage(1)
            }}
            className="mt-4 px-4 max-md:px-0"
          />

          <DddFilesDownloadBar
            selectedCount={selectedFileIds.length}
            onDownload={handleDownloadSelected}
            onClear={() => setSelectedFileIds([])}
            isDownloading={downloadMutation.isPending}
            visible={selectedFileIds.length > 0}
          />
        </CardContent>
      </Card>
    </div>
  )
}
